import type { ReactNode } from 'react'
import { cn } from './cn'

/** The page's measure: 80rem, the same as the WyseAi site. */
export const CONTAINER = 'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'

/**
 * Display headings, in Cabinet Grotesk. WyseHub's own face, which the product already
 * uses for its hero; the body stays in the system stack.
 */
export const DISPLAY_H2_BASE =
  'font-display text-3xl font-extrabold leading-[1.1] tracking-tight sm:text-4xl xl:text-5xl'

export const DISPLAY_H2 = `${DISPLAY_H2_BASE} text-ink`

export const CARD_HEADING = 'font-display font-bold tracking-tight text-ink'

/**
 * The page's vertical rhythm: the hero and the close breathe more than the middle, the
 * dark band a little more than its neighbours.
 */
const SIZES = {
  hero: ['pt-16 lg:pt-24', 'pb-24 lg:pb-32'],
  default: ['pt-20 lg:pt-24', 'pb-20 lg:pb-24'],
  dark: ['pt-24 lg:pt-28', 'pb-24 lg:pb-28'],
  close: ['pt-28 lg:pt-36', 'pb-28 lg:pb-36'],
} as const

const TONES = {
  surface: 'bg-surface',
  muted: 'bg-surface-muted',
  ink: 'bg-ink-deep',
} as const

/**
 * A full-width band with a centred column inside it. The page is built entirely from
 * these: white and grey alternating, one dark band, every card the inverse tone of the
 * ground it sits on.
 */
export function Band({
  children,
  tone = 'surface',
  size = 'default',
  id,
  className,
}: {
  readonly children: ReactNode
  readonly tone?: keyof typeof TONES
  readonly size?: keyof typeof SIZES
  readonly id?: string
  readonly className?: string
}) {
  const [top, bottom] = SIZES[size]
  return (
    <section id={id} className={cn(TONES[tone], className)}>
      <div className={cn(CONTAINER, top, bottom)}>{children}</div>
    </section>
  )
}

/**
 * A card with no border: contrast with the band is what makes it a card, so the tone is
 * always the inverse of the ground and is passed in rather than defaulted.
 */
export function MarketingCard({
  children,
  tone,
  className,
  enterIndex,
}: {
  readonly children: ReactNode
  readonly tone: 'surface' | 'muted'
  readonly className?: string
  readonly enterIndex?: number
}) {
  const entering = enterIndex !== undefined
  return (
    <div
      className={cn(
        'lift rounded-card p-6',
        tone === 'surface' ? 'bg-surface' : 'bg-surface-muted',
        entering && 'enter',
        className,
      )}
      // biome-ignore lint/plugin: the stagger index is a custom property the entrance keyframe reads
      style={
        entering
          ? ({ '--enter-index': enterIndex } as React.CSSProperties)
          : undefined
      }
    >
      {children}
    </div>
  )
}
